const SCHEME = /^([a-z][a-z0-9+.-]*):(?!\d)/i

const WEB_SCHEMES: ReadonlySet<string> = new Set(['http', 'https'])

const FORBIDDEN_CHARACTER = /[\s\\]/

const AUTHORITY_END = /[/?#]/

const IPV4_HOST = /^\d+\.\d+\.\d+\.\d+$/

const TOP_LEVEL_LABEL = /^(?:[a-z]{2,}|xn--[a-z0-9-]+)$/

/** Splits typed text into the part after the scheme, or null when it names a scheme other than http or https. */
function stripScheme(input: string): string | null {
  if (input.startsWith('//')) return input.slice(2)
  const scheme = SCHEME.exec(input)
  if (scheme === null) return input
  const name = scheme[1]?.toLowerCase() ?? ''
  if (!WEB_SCHEMES.has(name)) return null
  return input.slice(scheme[0].length).replace(/^\/*/, '')
}

/** True when a parsed host names a public website: two or more labels, a real top-level label, no IP literal, no localhost. */
function isPublicHost(hostname: string): boolean {
  if (hostname.startsWith('[') || IPV4_HOST.test(hostname)) return false
  const labels = hostname.split('.')
  if (labels.length < 2 || labels.some((label) => label === '')) return false
  if (labels.includes('localhost')) return false
  return TOP_LEVEL_LABEL.test(labels[labels.length - 1] ?? '')
}

/** Turns a typed website address into `https://host/path`; null when the text is not a public web address. */
export function normalizeWebAddress(input: string): string | null {
  const trimmed = input.trim()
  if (trimmed === '' || FORBIDDEN_CHARACTER.test(trimmed)) return null

  const rest = stripScheme(trimmed)
  if (rest === null || rest === '') return null

  const authorityEnd = rest.search(AUTHORITY_END)
  const authority = authorityEnd === -1 ? rest : rest.slice(0, authorityEnd)
  const tail = authorityEnd === -1 ? '' : rest.slice(authorityEnd)
  if (authority === '' || authority.includes('@')) return null

  const address = `https://${authority.toLowerCase()}${tail === '/' ? '' : tail}`
  if (!URL.canParse(address)) return null
  const parsed = new URL(address)
  if (parsed.username !== '' || parsed.password !== '') return null
  return isPublicHost(parsed.hostname) ? address : null
}
